import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import CreatableSelect from 'react-select/creatable';
import store from '../stores/NotesStore';
import { searchMetadata, createMetadata, updateEntryIdentity } from '../helpers/api';
import { findBestMetadataMatch } from '../helpers/metadataHelpers';
import './MetadataLinkModal.css';

const MetadataLinkModal = observer(({ entry, onClose }) => {
  const [metadataOptions, setMetadataOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  // Determine entry kind from value
  const entryKind = entry.value < 0 ? 'expense' : 'income';

  const loadMetadata = useCallback(async () => {
    try {
      setLoading(true);
      const results = await searchMetadata(store.userId, { kind: entryKind });
      const options = results.map(meta => ({
        value: meta.id,
        label: meta.name,
        isExisting: true
      }));
      setMetadataOptions(options);
    } catch (error) {
      console.error('Error loading metadata:', error);
    } finally {
      setLoading(false);
    }
  }, [entryKind]);

  // Load metadata and set initial value when entry changes
  useEffect(() => {
    const initializeModal = async () => {
      await loadMetadata();

      // Set initial value
      if (entry.metadataId) {
        // Already has metadata linked - pre-select it
        const metadataName = store.getMetadataName(entry.metadataId);
        if (metadataName) {
          setSelectedOption({
            value: entry.metadataId,
            label: metadataName,
            isExisting: true
          });
        }
      } else {
        // No metadata linked yet - check for similar existing metadata
        const labelToMatch = entry.customName || entry.label || '';

        if (labelToMatch && labelToMatch.trim()) {
          // Get all metadata from store
          const allMetadata = Array.from(store.metadata.values());

          // Find best match
          const bestMatch = findBestMetadataMatch(labelToMatch, allMetadata, entryKind);

          if (bestMatch) {
            // Found a good match - pre-select it in dropdown
            console.log('[MetadataLink] Found similar metadata:', bestMatch.name, 'score:', bestMatch.similarityScore);
            setSelectedOption({
              value: bestMatch.id,
              label: bestMatch.name,
              isExisting: true
            });
          } else {
            // No good match - pre-select "create new" option in dropdown
            console.log('[MetadataLink] No similar metadata found, pre-selecting create option:', labelToMatch);
            setSelectedOption({
              value: labelToMatch,
              label: labelToMatch,
              isExisting: false  // This will trigger metadata creation
            });
          }
        }
      }
    };

    initializeModal();
  }, [entry.metadataId, entry.customName, entry.label, loadMetadata, entryKind]);

  const handleSave = async () => {
    setLoading(true);
    try {
      console.log('[MetadataLink] handleSave called', {
        selectedOption,
        entryKind,
        userId: store.userId,
        noteId: store.currentNote?.id,
        entryId: entry.id
      });

      if (!selectedOption) {
        console.warn('[MetadataLink] No metadata selected');
        alert('Por favor, selecione ou crie um cadastro');
        setLoading(false);
        return;
      }

      // Either existing or new metadata
      if (selectedOption.isExisting) {
        // Link to existing metadata
        console.log('[MetadataLink] Linking to existing metadata:', selectedOption.value);
        await updateEntryIdentity(
          store.userId,
          store.currentNote.id,
          entry.id,
          {
            metadataId: selectedOption.value,
            kind: entryKind
          }
        );
      } else {
        // Create new metadata and link
        console.log('[MetadataLink] Creating new metadata:', selectedOption.label);
        const result = await createMetadata(store.userId, {
          name: selectedOption.label,
          kind: entryKind
        });
        console.log('[MetadataLink] Created metadata:', result);

        console.log('[MetadataLink] Linking entry to new metadata:', result.metadata.id);
        await updateEntryIdentity(
          store.userId,
          store.currentNote.id,
          entry.id,
          {
            metadataId: result.metadata.id,
            kind: entryKind
          }
        );
      }

      // Reload note and metadata
      await Promise.all([
        store.loadNoteDetails(store.currentNote.id),
        store.loadMetadata()
      ]);

      onClose();
    } catch (error) {
      console.error('Error saving metadata link:', error);
      alert('Erro ao salvar vinculação');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLink = async () => {
    if (!window.confirm('Remover vínculo com cadastro?')) return;

    setLoading(true);
    try {
      // Convert to custom name with current label
      const currentLabel = store.getMetadataName(entry.metadataId) || entry.customName || entry.label || 'Sem nome';

      await updateEntryIdentity(
        store.userId,
        store.currentNote.id,
        entry.id,
        {
          customName: currentLabel,
          kind: entryKind
        }
      );

      await store.loadNoteDetails(store.currentNote.id);
      onClose();
    } catch (error) {
      console.error('Error removing link:', error);
      alert('Erro ao remover vínculo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="metadata-link-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Vincular Cadastro</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          <div className="form-section">
            <label className="form-label">
              Cadastro ({entryKind === 'expense' ? 'Despesa' : 'Receita'})
            </label>
            <CreatableSelect
              isClearable
              isDisabled={loading}
              isLoading={loading}
              onChange={(newValue) => {
                console.log('[MetadataLink] onChange called with:', newValue);
                setSelectedOption(newValue);
              }}
              onCreateOption={(inputValue) => {
                console.log('[MetadataLink] onCreateOption called with:', inputValue);
                const newOption = {
                  value: inputValue,
                  label: inputValue,
                  isExisting: false
                };
                console.log('[MetadataLink] Setting selectedOption to:', newOption);
                setSelectedOption(newOption);
              }}
              options={metadataOptions}
              value={selectedOption}
              placeholder="Selecione ou digite para criar..."
              formatCreateLabel={(inputValue) => `Criar "${inputValue}"`}
              noOptionsMessage={() => 'Nenhum cadastro encontrado'}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 9999
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999
                })
              }}
              menuPortalTarget={document.body}
            />
            {selectedOption && !selectedOption.isExisting && (
              <span className="new-badge">novo</span>
            )}
            <div className="hint">
              Selecione um cadastro existente ou digite para criar um novo
            </div>
          </div>
        </div>

        <div className="modal-footer">
          {entry.metadataId && (
            <button
              className="remove-btn"
              onClick={handleRemoveLink}
              disabled={loading}
            >
              Desvincular
            </button>
          )}
          <div className="footer-actions">
            <button className="cancel-btn" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button className="save-btn" onClick={handleSave} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default MetadataLinkModal;
